import type { HeroThemeId } from "@/lib/types";

export type HeroContent = {
  badge?: string;
  title: string;
  subtitle?: string;
  primaryCta?: { label: string; href: string };
  secondaryCta?: { label: string; href: string };
};

export type HeroThemeMeta = {
  id: HeroThemeId;
  label: string;
  description: string;
};

/** Maps removed themes to their closest replacement in saved CMS/settings data. */
const LEGACY_HERO_THEME: Record<string, HeroThemeId> = {
  particles: "flux",
  "neon-grid": "orbit",
};

export const HERO_THEMES: readonly HeroThemeMeta[] = [
  {
    id: "aurora",
    label: "Aurora",
    description:
      "Soft brand gradients with floating light — balanced default for enterprise sites.",
  },
  {
    id: "glass",
    label: "Glass Layers",
    description:
      "Frosted panels and a light sweep — modern SaaS, premium and restrained.",
  },
  {
    id: "spotlight",
    label: "Spotlight",
    description:
      "Mesh gradient with cursor-follow highlight — interactive and contemporary.",
  },
  {
    id: "orbit",
    label: "Orbit Network",
    description:
      "Concentric rings and nodes — connectivity, platforms, and scale.",
  },
  {
    id: "flux",
    label: "Data Flux",
    description:
      "Flowing light bands — innovation and motion without visual noise.",
  },
  {
    id: "minimal",
    label: "Editorial Light",
    description:
      "Light background, typographic reveal — calm, editorial, accessibility-friendly.",
  },
] as const;

export const DEFAULT_HERO_THEME: HeroThemeId = "aurora";

export function resolveHeroTheme(value: unknown): HeroThemeId {
  if (typeof value !== "string") return DEFAULT_HERO_THEME;
  const v = value.trim().toLowerCase();
  const legacy = LEGACY_HERO_THEME[v];
  if (legacy) return legacy;
  return (HERO_THEMES.find((t) => t.id === v)?.id ??
    DEFAULT_HERO_THEME) as HeroThemeId;
}
